import { Toaster } from "sonner";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import Sidebar from "./components/Sidebar.jsx";
import PlanGate from "./components/PlanGate.jsx";
import icon from "./assets/icon-outline.svg";
import Dashboard from "./pages/Dashboard.jsx";
import Incidents from "./pages/Incidents.jsx";
import Invite from "./pages/Invite.jsx";
import Login from "./pages/Login.jsx";
import Signup from "./pages/Signup.jsx";
import VerifyEmail from "./pages/VerifyEmail.jsx";
import Maintenance from "./pages/Maintenance.jsx";
import Members from "./pages/Members.jsx";
import Channels from "./pages/Channels.jsx";
import GroupDetail from "./pages/GroupDetail.jsx";
import Groups from "./pages/Groups.jsx";
import Sites from "./pages/Sites.jsx";
import SiteDetail from "./pages/SiteDetail.jsx";
import Monitor from "./pages/Monitor.jsx";
import Monitors from "./pages/Monitors.jsx";
import ResetPassword from "./pages/ResetPassword.jsx";
import Settings from "./pages/Settings.jsx";
import Billing from "./pages/Billing.jsx";
import StatusPages from "./pages/StatusPages.jsx";
import User from "./pages/User.jsx";
import { useEffect, useState } from "react";
import AuditLog from "./pages/AuditLog.jsx";
import OnCall from "./pages/OnCall.jsx";
import InboundWebhooks from "./pages/InboundWebhooks.jsx";
import Workspaces from "./pages/Workspaces.jsx";
import WorkspacePicker from "./pages/WorkspacePicker.jsx";
import Wizard from "./pages/Wizard.jsx";
import Rum from "./pages/Rum.jsx";
import Servers from "./pages/Servers.jsx";
import ServerDetail from "./pages/ServerDetail.jsx";
import Teams from "./pages/Teams.jsx";
import Services from "./pages/Services.jsx";
import RoutingRules from "./pages/RoutingRules.jsx";
import { fetchMe } from "./api/me.js";
import { refreshSession } from "./api/auth.js";
import { useAuthStore } from "./store/authStore.js";
import { AIAssistantButton } from "./components/ai/index.js";

/**
 * Layout for authenticated pages.
 * On mount, if there is no access token but user identity is in sessionStorage,
 * attempt a silent refresh before deciding to redirect.
 */
function ProtectedLayout() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const workspace = useAuthStore((s) => s.workspace);
  const setSession = useAuthStore((s) => s.setSession);
  const logout = useAuthStore((s) => s.logout);
  const setPermissions = useAuthStore((s) => s.setPermissions);
  // Always attempt a silent refresh on mount when we have no access token —
  // the refresh-token cookie may still be valid (new tab, restored window,
  // cleared localStorage but live session).  We only skip this when an access
  // token is already in memory.
  const [initializing, setInitializing] = useState(!accessToken);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();

  // Close mobile nav on route change (e.g. browser back)
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (accessToken) return;
    refreshSession()
      .then((session) => setSession(session))
      .catch(() => {
        // No live session — clear any stale persisted identity so the
        // /login redirect below kicks in cleanly.
        if (user) logout();
      })
      .finally(() => setInitializing(false));
  }, []);

  useEffect(() => {
    if (!accessToken) return;
    fetchMe()
      .then((me) => {
        if (me.permissions) setPermissions(me.permissions, me.role);
      })
      .catch(() => {});
  }, [accessToken]);

  if (initializing) {
    return null;
  }

  if (!accessToken) {
    return <Navigate to="/login" replace />;
  }

  // If no active workspace and not already on the picker, send to workspace picker
  if (!workspace && location.pathname !== "/pick-workspace") {
    return <Navigate to="/pick-workspace" replace />;
  }

  if (location.pathname === "/pick-workspace") {
    return <Outlet />;
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Mobile top bar — hidden on md+ */}
        <header className="flex h-14 items-center gap-3 border-b border-gray-200 bg-white px-4 md:hidden">
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            className="rounded p-1.5 text-gray-500 hover:bg-gray-100"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <img src={icon} height="24" width="24" />
          <span className="text-sm font-bold tracking-wide text-teal-600">Monitero</span>
        </header>
        <main key={workspace?.id} className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 sm:py-6">
            <Outlet />
          </div>
        </main>
      </div>
      <AIAssistantButton />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/invite/:token" element={<Invite />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        <Route element={<ProtectedLayout />}>
          <Route path="/pick-workspace" element={<WorkspacePicker />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/wizard" element={<Wizard />} />
          <Route path="/rum" element={<PlanGate feature="rum"><Rum /></PlanGate>} />
          <Route path="/servers" element={<PlanGate feature="servers"><Servers /></PlanGate>} />
          <Route path="/servers/:id" element={<PlanGate feature="servers"><ServerDetail /></PlanGate>} />
          <Route path="/groups" element={<PlanGate feature="groups"><Groups /></PlanGate>} />
          <Route path="/groups/:id" element={<PlanGate feature="groups"><GroupDetail /></PlanGate>} />
          <Route path="/sites" element={<Sites />} />
          <Route path="/sites/:id" element={<SiteDetail />} />
          <Route path="/monitors" element={<Monitors />} />
          <Route path="/monitors/:id" element={<Monitor />} />
          <Route path="/incidents" element={<Incidents />} />
          <Route path="/maintenance-windows" element={<Maintenance />} />
          <Route path="/channels" element={<Channels />} />
          <Route path="/team" element={<Members />} />
          <Route path="/workspaces" element={<Workspaces />} />
          <Route path="/status-pages" element={<StatusPages />} />
          <Route path="/user" element={<User />} />
          <Route path="/audit-log" element={<AuditLog />} />
          <Route path="/on-call" element={<PlanGate feature="on_call"><OnCall /></PlanGate>} />
          <Route path="/inbound-webhooks" element={<InboundWebhooks />} />
          <Route path="/teams" element={<PlanGate feature="operations"><Teams /></PlanGate>} />
          <Route path="/services" element={<PlanGate feature="operations"><Services /></PlanGate>} />
          <Route path="/routing-rules" element={<PlanGate feature="operations"><RoutingRules /></PlanGate>} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/billing" element={<Billing />} />
        </Route>

        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
